import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { LicenseMode, LicenseState, ProFeature, TrialState } from '../types';
import {
  getTrialState,
  shouldShowTrialReminder,
  shouldShowTrialCard,
  getTrialDaysRemaining,
  createTrialStartState,
  createTrialSkipState,
  classifyServerTrialStart,
} from '../services/trialService';
import { isBrowserModePro } from '../../../features/browser-mode/browserModeUtils';
import {
  selectIsPro as selectIsSubscriptionPro,
  selectIsInGracePeriod,
  useSubscriptionStore,
} from './subscriptionStore';
import { useAuthStore } from './authStore';
import { createGuardedJsonStorage } from '../../storage/guardedJsonStorage';

/**
 * Track trial analytics event from store (non-React context).
 * Fire-and-forget pattern - analytics errors are silently ignored.
 */
function trackTrialEvent(eventName: string, data: Record<string, string> = {}): void {
  if (typeof window === 'undefined') return;
  if (!window.electronAPI?.analytics?.track) return;

  window.electronAPI.analytics.track(eventName, data).catch(() => {
    // Silently ignore analytics errors
  });
}

/**
 * Check if this build was started with Pro mode enabled.
 *
 * IMPORTANT:
 * - In the renderer, `import.meta.env.VITE_PRO_MODE` is a compile-time flag from Vite,
 *   not a runtime environment variable. Users cannot toggle it without rebuilding.
 * - We still do NOT trust persisted `mode` for Personal builds (see `initialize`).
 * - SECURITY: Only allow VITE_PRO_MODE in DEV mode to prevent accidental production builds
 *   from granting Pro access. Production Pro access must come from subscription/trial.
 */
const isProBuild = (): boolean => {
  // SECURITY: Only allow VITE_PRO_MODE in development/test mode
  // This prevents a misbuild with VITE_PRO_MODE=true from granting Pro in production
  if (!import.meta.env.DEV) return false;
  return import.meta.env.VITE_PRO_MODE === 'true';
};

/**
 * Initial license mode based on environment variable.
 */
const getInitialMode = (): LicenseMode => {
  return isProBuild() ? 'pro' : 'personal';
};

/**
 * Get the current user ID from auth store.
 * Used for account-based trial verification.
 */
function getCurrentUserId(): string | undefined {
  return useAuthStore.getState().user?.id;
}

/**
 * Normalize a persisted trial date to epoch ms, or undefined if absent/invalid.
 *
 * The `LicenseState` type declares these fields as `Date`, but zustand's
 * `persist` middleware round-trips through `JSON.stringify`/`JSON.parse`
 * with no custom (de)serializer, so after rehydration they are actually
 * ISO date **strings** at runtime. `new Date(...)` accepts both a `Date`
 * and a date string, so this is safe for either representation.
 */
function toEpochMs(value: Date | string | undefined): number | undefined {
  if (value === undefined) return undefined;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? undefined : time;
}

interface LicenseStore {
  // State
  state: LicenseState;
  isInitialized: boolean;

  // Computed helpers
  isPro: () => boolean;
  canUseFeature: (feature: ProFeature) => boolean;

  // Trial helpers
  getTrialState: () => TrialState;
  getTrialDaysRemaining: () => number;
  shouldShowTrialReminder: () => boolean;
  /** Whether the passive start-page trial card should be shown (respects "Maybe later" snooze) */
  shouldShowTrialCard: () => boolean;

  // Actions
  initialize: () => void;

  // Trial actions
  startTrial: () => void;
  /** Start trial with server-provided dates (server-backed trial) */
  startTrialFromServer: (startedAt: Date, expiresAt: Date, userId?: string, isNew?: boolean) => void;
  skipTrial: () => void;
  dismissTrialReminder: () => void;
  /** Snooze the start-page trial card ("Maybe later") for TRIAL_CARD_DISMISS_INTERVAL_DAYS */
  dismissTrialCard: () => void;
  /** Clear trial state (used on sign-out for account-based trial) */
  clearTrialState: () => void;
}

export const useLicenseStore = create<LicenseStore>()(
  persist(
    (set, get) => ({
      // Initial state
      state: {
        mode: getInitialMode(),
      },
      isInitialized: false,

      // Computed: Check if currently in Pro mode
      // Pro access is derived from legitimate sources only - never from persisted 'mode' field.
      // This prevents users from editing localStorage to grant themselves Pro access.
      isPro: () => {
        // Browser mode always has Pro features enabled for SQLite showcase
        if (isBrowserModePro()) {
          return true;
        }

        // Check if trial is active (derived from trial dates, not persisted mode)
        // ACCOUNT-BASED: Pass current user ID to verify trial ownership
        const currentUserId = getCurrentUserId();
        const trialActive = getTrialState(get().state, currentUserId) === 'active';

        // Check subscription/grace period from subscription store
        const subscriptionState = useSubscriptionStore.getState();
        const hasSubscriptionPro = selectIsSubscriptionPro(subscriptionState);
        const hasGracePeriod = selectIsInGracePeriod(subscriptionState);

        // Pro builds can run in Personal mode (user selection), so Pro is granted only
        // when `mode` is explicitly set to 'pro' (tests can force Personal mode).
        if (isProBuild() && get().state.mode === 'pro') {
          return true;
        }

        return trialActive || hasSubscriptionPro || hasGracePeriod;
      },

      // Computed: Check if a specific Pro feature is available
      // Pro access is derived from legitimate sources only.
      canUseFeature: (_feature: ProFeature) => {
        // Browser mode: all Pro features enabled
        if (isBrowserModePro()) {
          return true;
        }

        // Pro access is a combination of:
        // - Active trial (derived from dates, not persisted mode)
        // - Subscription active
        // - Grace period
        // - Pro build with explicit 'pro' mode
        // ACCOUNT-BASED: Pass current user ID to verify trial ownership
        const currentUserId = getCurrentUserId();
        const trialActive = getTrialState(get().state, currentUserId) === 'active';
        const subscriptionState = useSubscriptionStore.getState();
        const hasSubscriptionPro = selectIsSubscriptionPro(subscriptionState);
        const hasGracePeriod = selectIsInGracePeriod(subscriptionState);

        if (isProBuild() && get().state.mode === 'pro') {
          return true;
        }

        void _feature; // Suppress unused variable warnings
        return trialActive || hasSubscriptionPro || hasGracePeriod;
      },

      // Trial: Get current trial state
      // ACCOUNT-BASED: Pass current user ID to verify trial ownership
      getTrialState: () => getTrialState(get().state, getCurrentUserId()),

      // Trial: Get days remaining in trial
      getTrialDaysRemaining: () => getTrialDaysRemaining(get().state),

      // Trial: Check if reminder should be shown
      shouldShowTrialReminder: () => shouldShowTrialReminder(get().state),

      // Trial: Check if the passive start-page card should be shown
      // ACCOUNT-BASED: Pass current user ID to verify trial ownership
      shouldShowTrialCard: () => shouldShowTrialCard(get().state, getCurrentUserId()),

      // Action: Initialize store (called on app start)
      initialize: () => {
        const envMode = getInitialMode();
        const currentState = get().state;
        // Record the install's first app run once (gates the trial onboarding
        // dialog to the second calendar day; see isTrialOnboardingDue).
        const firstSeenAt = currentState.firstSeenAt ?? Date.now();

        // Personal builds: force Personal mode (no Pro features available).
        // Pro builds: respect persisted/user-selected mode (personal/trial/subscription).
        set({
          state:
            envMode === 'personal'
              ? { ...currentState, mode: 'personal', firstSeenAt }
              : import.meta.env.DEV
                ? {
                    ...currentState,
                    mode: 'pro',
                    trialSkipped: false,
                    firstSeenAt,
                  }
                : { ...currentState, firstSeenAt },
          isInitialized: true,
        });
      },

      // Trial Action: Start the 14-day trial (local dates)
      startTrial: () => {
        const trialState = createTrialStartState();
        set({
          state: {
            ...get().state,
            ...trialState,
          },
        });
        trackTrialEvent('auth:trial_start');
      },

      // Trial Action: Start trial with server-provided dates (server-backed trial)
      // The dates come from the server's trial record, making them tamper-resistant
      // ACCOUNT-BASED: Store the user ID to prevent trial leakage between accounts
      //
      // This is called both from genuine "start trial" user actions AND from
      // background sync paths (useSignedTokenSync, authStore's post-sign-in
      // restore) that re-sync trial dates on every token refresh / visibility
      // change. To keep 'auth:trial_start_server' meaningful ("user started a
      // trial") instead of firing on every sync, this action is idempotent:
      //   - Identical (user, startedAt, expiresAt) vs. current state: full
      //     no-op (no set(), no event).
      //   - Same user, dates differ (e.g. admin-extended expiry): update the
      //     store silently, no event.
      //   - No existing trial dates, or a different trialUserId: genuine
      //     start, update the store AND fire the event.
      startTrialFromServer: (startedAt: Date, expiresAt: Date, userId?: string, isNew?: boolean) => {
        // Use provided userId or get from auth store
        const trialUserId = userId ?? getCurrentUserId();
        const currentState = get().state;

        const currentStartedAt = toEpochMs(currentState.trialStartedAt);
        const currentExpiresAt = toEpochMs(currentState.trialExpiresAt);
        const hasExistingTrial = currentStartedAt !== undefined && currentExpiresAt !== undefined;
        const sameUser = currentState.trialUserId === trialUserId;
        const sameDates =
          currentStartedAt === startedAt.getTime() && currentExpiresAt === expiresAt.getTime();

        // Full no-op: nothing about the trial actually changed.
        if (hasExistingTrial && sameUser && sameDates) {
          return;
        }

        set({
          state: {
            ...currentState,
            trialStartedAt: startedAt,
            trialExpiresAt: expiresAt,
            trialSkipped: false,
            trialUserId: trialUserId,
            mode: 'pro', // During trial, user has Pro access
          },
        });

        // Genuine start: either there was no prior trial recorded, or the
        // trial now belongs to a different account. A same-user date-only
        // change (server-side expiry extension) stays silent. `kind` tells a
        // trial the server CREATED in this action (`new`) from one it handed
        // back from an earlier start (`restored`) — the server's own
        // `trial.is_new`, threaded in by the start-trial callers; sync paths
        // pass nothing and are `restored`.
        const isGenuineStart = !hasExistingTrial || !sameUser;
        if (isGenuineStart) {
          trackTrialEvent('auth:trial_start_server', { kind: classifyServerTrialStart(isNew) });
        }
      },

      // Trial Action: Skip trial (choose Personal mode)
      // Also sets trialReminderDismissedAt to start the 7-day countdown
      // so the reminder doesn't appear immediately after skipping
      skipTrial: () => {
        const skipState = createTrialSkipState();
        set({
          state: {
            ...get().state,
            ...skipState,
            trialReminderDismissedAt: new Date(), // Start 7-day countdown from skip
          },
        });
        trackTrialEvent('auth:trial_skip');
      },

      // Trial Action: Dismiss the trial reminder
      dismissTrialReminder: () => {
        set({
          state: {
            ...get().state,
            trialReminderDismissedAt: new Date(),
          },
        });
      },

      // Trial Action: Snooze the passive start-page card ("Maybe later").
      // Unlike skipTrial (permanent Personal choice), this only hides the card
      // for TRIAL_CARD_DISMISS_INTERVAL_DAYS; the trial stays 'not-started'.
      dismissTrialCard: () => {
        set({
          state: {
            ...get().state,
            trialCardDismissedAt: new Date(),
          },
        });
      },

      // Trial Action: Clear trial state (used on sign-out)
      // ACCOUNT-BASED: Trial is tied to a user account, so we clear it on logout
      // This prevents trial from being used while signed out or by another user
      clearTrialState: () => {
        set({
          state: {
            ...get().state,
            trialStartedAt: undefined,
            trialExpiresAt: undefined,
            trialSkipped: undefined,
            trialReminderDismissedAt: undefined,
            trialUserId: undefined,
            mode: 'personal',
          },
        });
      },
    }),
    {
      name: 'sql-studio-license',
      // Crash-proof write (core/storage/guardedJsonStorage.ts): a full origin must not throw out of
      // set(). No toast here — it hydrates at startup and the licence/trial state is re-derived
      // from the licence service; the drop is still reported to telemetry and the console.
      storage: createGuardedJsonStorage(),
      partialize: (state) => ({
        state: {
          mode: state.state.mode,
          // Trial fields
          trialStartedAt: state.state.trialStartedAt,
          trialExpiresAt: state.state.trialExpiresAt,
          trialSkipped: state.state.trialSkipped,
          trialReminderDismissedAt: state.state.trialReminderDismissedAt,
          trialCardDismissedAt: state.state.trialCardDismissedAt,  // "Maybe later" snooze
          trialUserId: state.state.trialUserId,  // ACCOUNT-BASED: Track trial ownership
          firstSeenAt: state.state.firstSeenAt,  // Install's first run (trial dialog day-gate)
        },
      }),
      onRehydrateStorage: () => {
        return (state: LicenseStore | undefined) => {
          // Re-check env var after rehydration
          if (state) {
            state.initialize();
          }
        };
      },
    }
  )
);
